/**
 * AR-5 Turn Shape Quality Algorithm
 * Method ID: b1-turn-quality
 *
 * Evaluates turn roundness and left-right symmetry by integrating the
 * acceleration X/Y trajectory, fitting a circle to the detrended path,
 * and comparing the first half vs the second half of the turn.
 *
 * 评估弯形圆润度与左右对称度：对加速度 X/Y 分量进行二次积分得到轨迹，
 * 去除线性漂移后做代数最小二乘圆拟合，并对比弯的前半段与后半段。
 *
 * Deterministic per PO-5.4 (no random numbers).
 * 按 PO-5.4 确定性实现（无随机数）。
 */

import { AR5_PARAMS } from '../constants/algorithm-params';
import type { SkiTickData } from '../types/ski-data';

// ===== Output Interface =====

/** AR-5 弯形质量结果 / Turn shape quality result for a single turn */
export interface TurnShapeResult {
  score: number;                    // 0-100 综合评分
  confidence: number;               // 0.85
  roundness: number;                // 0-100 圆润度
  symmetry: number;                 // 0-100 对称度
  fit_error: number;                // 平均拟合相对误差
  radius_cv: number;                // 点到圆心距离的变异系数
  fitted_center: { x: number; y: number } | null;  // 拟合圆心，拟合失败为 null
  fitted_radius: number | null;                     // 拟合半径，拟合失败为 null
  trajectory: { x: number; y: number }[];           // 去漂移后的轨迹
  duration_symmetry: number;        // 0-100 时长对称度
  edge_symmetry: number;            // 0-100 立刃对称度
  gyro_symmetry: number;            // 0-100 陀螺仪对称度
}

// ===== Derived Constants =====

/**
 * Integration timestep. The sensor samples at 100 Hz, so dt = 1/100 = 0.01 s.
 *
 * 积分时间步长。传感器采样率 100Hz，故 dt = 0.01s。
 */
const DT = 0.01;

/**
 * Minimum number of points required for algebraic circle fitting.
 *
 * 代数圆拟合所需的最少点数。
 */
const MIN_CIRCLE_FIT_POINTS = 5;

// --- Symmetry gates and linear decay slopes ---
// 对称度门控阈值与线性衰减斜率。
// Each sub-score is 100 when the diff is within its gate, then decays
// linearly to 0 at 3x the gate (i.e. 100 / (2 * gate) per unit).
// 各子分在差异不超门控时为 100，之后线性衰减至 3 倍门控处归零。

/** Duration diff gate: 100 ms. / 时长差异门控：100ms。 */
const DURATION_DIFF_THRESHOLD_MS = 100;
/** Duration decay slope: 0.5 per ms (reaches 0 at 300 ms). / 时长衰减斜率。 */
const DURATION_DECAY_PER_MS = 0.5;

/** Edge angle diff gate: 5 degrees. / 立刃角差异门控：5°。 */
const EDGE_DIFF_THRESHOLD_DEG = 5;
/** Edge decay slope: 10 per degree (reaches 0 at 15°). / 立刃衰减斜率。 */
const EDGE_DECAY_PER_DEG = 10;

/** Gyro Z diff gate: 0.2 rad/s. / 陀螺仪 Z 差异门控：0.2 rad/s。 */
const GYRO_DIFF_THRESHOLD = 0.2;
/** Gyro decay slope: 250 per rad/s (reaches 0 at 0.6 rad/s). / 陀螺仪衰减斜率。 */
const GYRO_DECAY_PER_RAD = 250;

// ===== Helper: Least Squares Solver =====

/**
 * Solve an overdetermined linear system A x ≈ b via the normal equations
 * (A^T A) x = A^T b followed by Gaussian elimination with partial pivoting.
 *
 * For the small 3×3 systems used by circle fitting, normal equations are
 * sufficient and deterministic; a full SVD is unnecessary.
 *
 * 通过正规方程 (A^T A) x = A^T b 求解超定线性方程组 A x ≈ b，
 * 再用部分主元高斯消元求解。对圆拟合的 3×3 小系统，正规方程已足够且确定。
 *
 * @param matrix - m×n coefficient matrix A (rows are samples) / m×n 系数矩阵
 * @param vector - length-m right-hand side b / 长度 m 的右端向量
 * @returns length-n least-squares solution x / 长度 n 的最小二乘解
 */
export function solveLeastSquaresSVD(
  matrix: number[][],
  vector: number[]
): number[] {
  const m = matrix.length;
  if (m === 0) return [];
  const n = matrix[0].length;

  // Build normal-equation matrices: ATA (n×n) and ATb (length n).
  // 构造正规方程矩阵：ATA（n×n）与 ATb（长度 n）。
  const ATA: number[][] = Array.from({ length: n }, () =>
    new Array(n).fill(0)
  );
  const ATb: number[] = new Array(n).fill(0);

  for (let k = 0; k < m; k++) {
    const row = matrix[k];
    const bk = vector[k];
    for (let i = 0; i < n; i++) {
      ATb[i] += row[i] * bk;
      for (let j = 0; j < n; j++) {
        ATA[i][j] += row[i] * row[j];
      }
    }
  }

  return solveLinearSystem(ATA, ATb);
}

/**
 * Solve a square linear system A x = b via Gaussian elimination with
 * partial pivoting. Returns a zero vector if the system is singular.
 *
 * 用部分主元高斯消元求解方阵线性方程组 A x = b。奇异时返回零向量。
 */
function solveLinearSystem(A: number[][], b: number[]): number[] {
  const n = A.length;
  // Build an augmented matrix (copy so the input is not mutated).
  // 构造增广矩阵（拷贝以免修改入参）。
  const M: number[][] = A.map((row, i) => [...row, b[i]]);

  for (let col = 0; col < n; col++) {
    // Partial pivot: pick the row with the largest magnitude in this column.
    // 部分主元：选取当前列绝对值最大的行。
    let pivot = col;
    let maxAbs = Math.abs(M[col][col]);
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(M[r][col]) > maxAbs) {
        maxAbs = Math.abs(M[r][col]);
        pivot = r;
      }
    }
    if (maxAbs === 0) {
      // Singular matrix; return a zero solution.
      // 奇异矩阵，返回零解。
      return new Array(n).fill(0);
    }
    if (pivot !== col) {
      const tmp = M[col];
      M[col] = M[pivot];
      M[pivot] = tmp;
    }

    // Eliminate rows below the pivot.
    // 消去主元下方各行。
    for (let r = col + 1; r < n; r++) {
      const factor = M[r][col] / M[col][col];
      for (let c = col; c <= n; c++) {
        M[r][c] -= factor * M[col][c];
      }
    }
  }

  // Back substitution.
  // 回代。
  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    let s = M[i][n];
    for (let j = i + 1; j < n; j++) {
      s -= M[i][j] * x[j];
    }
    x[i] = s / M[i][i];
  }
  return x;
}

// ===== Helper: Trajectory Integration =====

/**
 * Double-integrate acceleration components to a 2D trajectory.
 *
 *   vx = 0, vy = 0, x = 0, y = 0
 *   for each frame:
 *     vx += ax * dt
 *     vy += ay * dt
 *     x  += vx * dt
 *     y  += vy * dt
 *     trajectory.append((x, y))
 *
 * 对加速度分量进行二次积分得到二维轨迹。
 *
 * @param axSeries - acceleration X samples (m/s²) / X 轴加速度序列
 * @param aySeries - acceleration Y samples (m/s²) / Y 轴加速度序列
 * @param dt - integration timestep (s) / 积分时间步长（秒）
 * @returns trajectory points (one per input sample) / 轨迹点（每输入样本一个）
 */
export function integrateTrajectory(
  axSeries: number[],
  aySeries: number[],
  dt: number
): { x: number; y: number }[] {
  const trajectory: { x: number; y: number }[] = [];
  let vx = 0;
  let vy = 0;
  let x = 0;
  let y = 0;

  const n = Math.min(axSeries.length, aySeries.length);
  for (let i = 0; i < n; i++) {
    vx += axSeries[i] * dt;
    vy += aySeries[i] * dt;
    x += vx * dt;
    y += vy * dt;
    trajectory.push({ x, y });
  }
  return trajectory;
}

// ===== Helper: Detrend =====

/**
 * Remove the linear trend from each axis of the trajectory by ordinary
 * least-squares regression against the sample index t = 0..n-1, then
 * subtracting the fitted line. This removes integration drift before
 * circle fitting.
 *
 * 对轨迹的每个轴按样本序号 t = 0..n-1 做普通最小二乘线性回归并减去拟合直线，
 * 以消除积分漂移，便于后续圆拟合。
 *
 * @param trajectory - raw integrated trajectory / 原始积分轨迹
 * @returns detrended trajectory (linear trend removed) / 去漂移后的轨迹
 */
export function detrendTrajectory(
  trajectory: { x: number; y: number }[]
): { x: number; y: number }[] {
  const n = trajectory.length;
  if (n === 0) return [];
  if (n === 1) return [{ x: 0, y: 0 }]; // single point: trend = value → 0

  // Accumulate regression sums over t = 0..n-1.
  // 累加 t = 0..n-1 的回归求和量。
  let sumT = 0;
  let sumT2 = 0;
  let sumX = 0;
  let sumTX = 0;
  let sumY = 0;
  let sumTY = 0;
  for (let t = 0; t < n; t++) {
    sumT += t;
    sumT2 += t * t;
    sumX += trajectory[t].x;
    sumTX += t * trajectory[t].x;
    sumY += trajectory[t].y;
    sumTY += t * trajectory[t].y;
  }

  // denom = n*sumT2 - sumT² = n²·Var(t) > 0 for n > 1.
  // denom = n*sumT2 - sumT² = n²·Var(t)，n > 1 时恒正。
  const denom = n * sumT2 - sumT * sumT;
  let slopeX = 0;
  let interceptX = 0;
  let slopeY = 0;
  let interceptY = 0;
  if (denom !== 0) {
    slopeX = (n * sumTX - sumT * sumX) / denom;
    interceptX = (sumX - slopeX * sumT) / n;
    slopeY = (n * sumTY - sumT * sumY) / denom;
    interceptY = (sumY - slopeY * sumT) / n;
  }

  const detrended: { x: number; y: number }[] = [];
  for (let t = 0; t < n; t++) {
    detrended.push({
      x: trajectory[t].x - (slopeX * t + interceptX),
      y: trajectory[t].y - (slopeY * t + interceptY),
    });
  }
  return detrended;
}

// ===== Helper: Circle Fitting =====

/**
 * Fit a circle to 2D points using algebraic least squares.
 *
 * Circle equation: (x - cx)² + (y - cy)² = r²
 * Expanded:        x² + y² - 2·cx·x - 2·cy·y + (cx² + cy² - r²) = 0
 *
 * Substituting a = -2·cx, b = -2·cy, d = cx² + cy² - r² gives the linear
 * form:  a·x + b·y + d = x² + y²
 *
 * We build an N×3 system (rows [x, y, 1], rhs x² + y²) and solve it via
 * least squares, then recover:
 *   cx = -a/2,  cy = -b/2,  r = sqrt(cx² + cy² - d)
 *
 * Fit error is the mean of |distance_to_center - r| / r over all points.
 * Returns null when fewer than 5 points are supplied or the fit is
 * degenerate (negative or zero radius).
 *
 * 用代数最小二乘法对二维点集做圆拟合。
 * 拟合误差为各点 |到圆心距离 - r| / r 的均值。
 * 点数少于 5 或拟合退化（半径非正）时返回 null。
 *
 * @param points - 2D points to fit / 待拟合二维点
 * @returns fitted circle { cx, cy, r, fitError } or null / 拟合圆或 null
 */
export function fitCircle(
  points: { x: number; y: number }[]
): { cx: number; cy: number; r: number; fitError: number } | null {
  const n = points.length;
  if (n < MIN_CIRCLE_FIT_POINTS) return null;

  // Build the linear system A·[a, b, d]ᵀ = rhs.
  // 构造线性方程组 A·[a, b, d]ᵀ = rhs。
  const matrix: number[][] = [];
  const vector: number[] = [];
  for (let i = 0; i < n; i++) {
    const px = points[i].x;
    const py = points[i].y;
    matrix.push([px, py, 1]);
    vector.push(px * px + py * py);
  }

  const sol = solveLeastSquaresSVD(matrix, vector);
  const a = sol[0];
  const b = sol[1];
  const d = sol[2];

  const cx = -a / 2;
  const cy = -b / 2;
  const radicand = cx * cx + cy * cy - d;
  // Negative radicand → imaginary radius (degenerate fit).
  // Zero radius → cannot compute a meaningful relative error.
  // 负根号 → 虚半径（退化拟合）；零半径 → 无法计算相对误差。
  if (radicand <= 0) return null;
  const r = Math.sqrt(radicand);

  // Fit error: mean of |distance_to_center - r| / r.
  // 拟合误差：|到圆心距离 - r| / r 的均值。
  let sumRelErr = 0;
  for (let i = 0; i < n; i++) {
    const dx = points[i].x - cx;
    const dy = points[i].y - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    sumRelErr += Math.abs(dist - r) / r;
  }
  const fitError = sumRelErr / n;

  return { cx, cy, r, fitError };
}

// ===== Helper: Radius Coefficient of Variation =====

/**
 * Compute the coefficient of variation (CV = std / mean) of the distances
 * from each point to the given center. Uses population standard deviation.
 * Returns 0 when there are no points or the mean distance is zero.
 *
 * 计算各点到给定中心距离的变异系数（CV = 标准差 / 均值），采用总体标准差。
 * 无点或均值为零时返回 0。
 *
 * @param points - 2D points / 二维点
 * @param center - reference center / 参考中心
 * @returns coefficient of variation (dimensionless) / 变异系数（无量纲）
 */
export function computeRadiusCV(
  points: { x: number; y: number }[],
  center: { x: number; y: number }
): number {
  const n = points.length;
  if (n === 0) return 0;

  let sum = 0;
  const distances: number[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const dx = points[i].x - center.x;
    const dy = points[i].y - center.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    distances[i] = dist;
    sum += dist;
  }
  const mean = sum / n;
  if (mean === 0) return 0;

  let sqSum = 0;
  for (let i = 0; i < n; i++) {
    const diff = distances[i] - mean;
    sqSum += diff * diff;
  }
  const stdDev = Math.sqrt(sqSum / n); // population std dev / 总体标准差
  return stdDev / mean;
}

// ===== Internal: Symmetry Sub-Scores =====

/**
 * Linear-decay symmetry sub-score.
 *   diff <= gate  → 100
 *   otherwise     → max(0, 100 - (diff - gate) * decayPerUnit)
 *
 * 线性衰减对称度子分。
 */
function decaySymmetry(
  diff: number,
  gate: number,
  decayPerUnit: number
): number {
  if (diff <= gate) return 100;
  return Math.max(0, 100 - (diff - gate) * decayPerUnit);
}

// ===== Main Entry Point =====

/**
 * AR-5 Turn Shape Quality: evaluate turn roundness and left-right
 * symmetry for a single turn.
 * Method ID: b1-turn-quality
 *
 * Algorithm steps / 算法步骤:
 * 1. Extract acceleration X/Y over [startFrameIndex, endFrameIndex].
 *    提取 [startFrameIndex, endFrameIndex] 内加速度 X/Y。
 * 2. Double-integrate (dt = 0.01s) to a 2D trajectory, then detrend.
 *    二次积分（dt = 0.01s）得二维轨迹并去线性漂移。
 * 3. Algebraic least-squares circle fit → fit_error & radius_cv.
 *    代数最小二乘圆拟合 → 拟合误差与半径变异系数。
 * 4. Roundness = fit_score * 0.6 + cv_score * 0.4.
 *    圆润度 = 拟合分 * 0.6 + 变异分 * 0.4。
 * 5. Split the turn at its midpoint frame and compare first half vs
 *    second half: duration, mean edge angle, peak |gyro.z|.
 *    在中点帧切分弯，对比前半段与后半段：时长、平均立刃角、峰值 |gyro.z|。
 * 6. Symmetry = duration * 0.2 + edge * 0.4 + gyro * 0.4.
 *    对称度 = 时长 * 0.2 + 立刃 * 0.4 + 陀螺仪 * 0.4。
 * 7. Final score = roundness * 0.55 + symmetry * 0.45, confidence 0.85.
 *    最终得分 = 圆润度 * 0.55 + 对称度 * 0.45，置信度 0.85。
 *
 * Frame range [startFrameIndex, endFrameIndex] is inclusive on both ends.
 * 帧范围 [startFrameIndex, endFrameIndex] 两端均含。
 *
 * @param frames - array of SkiTickData frames / 滑雪帧数据数组
 * @param startFrameIndex - start frame index (inclusive) / 起始帧索引（含）
 * @param endFrameIndex - end frame index (inclusive) / 结束帧索引（含）
 * @returns TurnShapeResult / 弯形质量结果
 */
export function computeTurnShape(
  frames: SkiTickData[],
  startFrameIndex: number,
  endFrameIndex: number
): TurnShapeResult {
  // Default zero result. Confidence stays at the spec value (0.85).
  // 默认零结果，置信度保持规范值（0.85）。
  const zeroResult: TurnShapeResult = {
    score: 0,
    confidence: AR5_PARAMS.confidence,
    roundness: 0,
    symmetry: 0,
    fit_error: 0,
    radius_cv: 0,
    fitted_center: null,
    fitted_radius: null,
    trajectory: [],
    duration_symmetry: 0,
    edge_symmetry: 0,
    gyro_symmetry: 0,
  };

  // Clamp to valid inclusive range / 钳制到有效闭区间
  const lo = Math.max(0, Math.floor(startFrameIndex));
  const hi = Math.min(frames.length - 1, Math.floor(endFrameIndex));
  if (lo > hi || frames.length === 0) return zeroResult;

  const n = hi - lo + 1;

  // Step 1: extract acceleration X/Y series / 步骤 1：提取加速度 X/Y 序列
  const axSeries: number[] = [];
  const aySeries: number[] = [];
  for (let i = lo; i <= hi; i++) {
    axSeries.push(frames[i].acceleration.x);
    aySeries.push(frames[i].acceleration.y);
  }

  // Step 2: integrate + detrend / 步骤 2：积分 + 去漂移
  const rawTrajectory = integrateTrajectory(axSeries, aySeries, DT);
  const trajectory = detrendTrajectory(rawTrajectory);

  // Step 3 & 4: circle fit → roundness / 步骤 3 & 4：圆拟合 → 圆润度
  let fitError = 0;
  let radiusCV = 0;
  let roundness = 0;
  let fittedCenter: { x: number; y: number } | null = null;
  let fittedRadius: number | null = null;

  const fit = fitCircle(trajectory);
  if (fit !== null) {
    fitError = fit.fitError;
    fittedCenter = { x: fit.cx, y: fit.cy };
    fittedRadius = fit.r;
    radiusCV = computeRadiusCV(trajectory, fittedCenter);

    const fitTolerance = AR5_PARAMS.fitTolerance; // 0.15
    const cvThreshold = AR5_PARAMS.cvThreshold;   // 0.25

    // fit_score: 100 when error <= 0.15, else linear decay at 200/unit.
    // cv_score:  100 when cv <= 0.25, else linear decay at 200/unit.
    const fitScore =
      fitError <= fitTolerance
        ? 100
        : Math.max(0, 100 - (fitError - fitTolerance) * 200);
    const cvScore =
      radiusCV <= cvThreshold
        ? 100
        : Math.max(0, 100 - (radiusCV - cvThreshold) * 200);

    roundness = fitScore * 0.6 + cvScore * 0.4;
  }

  // Step 5: symmetry — split at the midpoint frame / 步骤 5：对称度——按中点帧切分
  // First half frames: [lo, mid), second half frames: [mid, hi].
  // 前半段帧：[lo, mid)，后半段帧：[mid, hi]。
  const mid = lo + Math.floor(n / 2);
  const hasTwoHalves = n >= 2 && mid > lo && hi >= mid;

  let durationSymmetry = 0;
  let edgeSymmetry = 0;
  let gyroSymmetry = 0;

  if (hasTwoHalves) {
    // --- Duration symmetry ---
    // First half spans frames [lo, mid-1]; second half spans [mid, hi].
    // 时长：前半段覆盖 [lo, mid-1]，后半段覆盖 [mid, hi]。
    const firstDuration = frames[mid - 1].timestamp_ms - frames[lo].timestamp_ms;
    const secondDuration = frames[hi].timestamp_ms - frames[mid].timestamp_ms;
    const durationDiff = Math.abs(firstDuration - secondDuration);
    durationSymmetry = decaySymmetry(
      durationDiff,
      DURATION_DIFF_THRESHOLD_MS,
      DURATION_DECAY_PER_MS
    );

    // --- Edge symmetry: mean edge angle per half ---
    // Per-frame edge angle = (left + right) / 2.
    // 立刃：每帧立刃角 = (左 + 右) / 2，再取半段均值。
    let firstEdgeSum = 0;
    let secondEdgeSum = 0;
    for (let i = lo; i < mid; i++) {
      firstEdgeSum += (frames[i].edge_angle_left + frames[i].edge_angle_right) / 2;
    }
    for (let i = mid; i <= hi; i++) {
      secondEdgeSum += (frames[i].edge_angle_left + frames[i].edge_angle_right) / 2;
    }
    const firstEdgeMean = firstEdgeSum / (mid - lo);
    const secondEdgeMean = secondEdgeSum / (hi - mid + 1);
    const edgeDiff = Math.abs(firstEdgeMean - secondEdgeMean);
    edgeSymmetry = decaySymmetry(
      edgeDiff,
      EDGE_DIFF_THRESHOLD_DEG,
      EDGE_DECAY_PER_DEG
    );

    // --- Gyro symmetry: peak |gyro.z| per half ---
    // 陀螺仪：各半段峰值 |gyro.z|。
    let firstGyroPeak = 0;
    let secondGyroPeak = 0;
    for (let i = lo; i < mid; i++) {
      const g = Math.abs(frames[i].gyroscope.z);
      if (g > firstGyroPeak) firstGyroPeak = g;
    }
    for (let i = mid; i <= hi; i++) {
      const g = Math.abs(frames[i].gyroscope.z);
      if (g > secondGyroPeak) secondGyroPeak = g;
    }
    const gyroDiff = Math.abs(firstGyroPeak - secondGyroPeak);
    gyroSymmetry = decaySymmetry(
      gyroDiff,
      GYRO_DIFF_THRESHOLD,
      GYRO_DECAY_PER_RAD
    );
  }

  const symmetry =
    durationSymmetry * 0.2 + edgeSymmetry * 0.4 + gyroSymmetry * 0.4;

  // Step 6 & 7: final score / 步骤 6 & 7：最终得分
  const score = roundness * 0.55 + symmetry * 0.45;
  const finalScore = Math.max(0, Math.min(100, score));

  return {
    score: finalScore,
    confidence: AR5_PARAMS.confidence,
    roundness,
    symmetry,
    fit_error: fitError,
    radius_cv: radiusCV,
    fitted_center: fittedCenter,
    fitted_radius: fittedRadius,
    trajectory,
    duration_symmetry: durationSymmetry,
    edge_symmetry: edgeSymmetry,
    gyro_symmetry: gyroSymmetry,
  };
}
